import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ---------------------------------------------------------------------------
// Manufacturer / protocol display helpers
//
// The DB stores 'mavlink' as a protocol category, not a real manufacturer.
// These helpers translate DB values into human-readable labels.
// ---------------------------------------------------------------------------

const MANUFACTURER_LABELS: Record<string, string> = {
  dji: 'DJI',
  skydio: 'Skydio',
  autel: 'Autel',
  mavlink: 'Open Source',
  other: 'Other',
};

/**
 * Get display label for a drone's manufacturer.
 * For MAVLink drones, extracts the autopilot name (ArduPilot/PX4) from the model field.
 */
export function getManufacturerLabel(manufacturer: string, model?: string | null): string {
  if (manufacturer === 'mavlink' && model) {
    const lower = model.toLowerCase();
    if (lower.includes('ardupilot') || lower.includes('arducopter') || lower.includes('arduplane')) return 'ArduPilot';
    if (lower.includes('px4')) return 'PX4';
  }
  return MANUFACTURER_LABELS[manufacturer] || manufacturer.toUpperCase();
}

/**
 * Get the protocol badge text (shown alongside manufacturer for MAVLink drones).
 */
export function getProtocolBadge(manufacturer: string): string | null {
  if (manufacturer === 'mavlink') return 'MAVLink';
  return null;
}

/**
 * True if a manufacturer is NDAA-restricted (Chinese-owned).
 * MAVLink/open-source drones are generally NDAA-compliant.
 */
export function isNdaaRestricted(manufacturer: string): boolean {
  return manufacturer === 'dji' || manufacturer === 'autel';
}

/**
 * Display label for the "Add Drone" manufacturer selector.
 */
export const MANUFACTURER_SELECT_LABELS: Record<string, string> = {
  dji: 'DJI',
  skydio: 'Skydio',
  autel: 'Autel',
  mavlink: 'Open Source (MAVLink)',
  other: 'Other',
};
